import type {CartesetDataHolder} from '../../../../types/CartesetDataHolder';

/**
 * זכות → חובה connections on the customer/supplier carteset.
 *
 * The allocation core (`allocateLedger`) is shared verbatim with the AI app
 * (`ai_assistent/frontend/src/components/dc/ledgerLinks.ts`) and ported
 * line-for-line to `ai_assistent/backend/debt_collection/analyzer_findings.py`.
 * Change all three together.
 *
 *  1. Marks on the receipt (`tax_note_id` → invoice ids + amount paid to each)
 *     are the truth. Whatever a mark does not cover stays open for the next
 *     passes; a marked invoice outside the date range still owns its share.
 *  2. A negative קבלה reverses the קבלה it cancels — both drop out. One that
 *     cancels nothing is a refund: a חובה that consumes surplus credit.
 *  3. Replay oldest → newest: exact-amount match first, then oldest open.
 *     Leftover credit is an advance taken by a later חובה the same way.
 *
 * Equal amounts in both columns = חשבונית מס קבלה (skipped). יתרת פתיחה with
 * both columns unequal is a net snapshot — the need is the difference.
 */

export type CartesetLink = {
    from: CartesetDataHolder;
    to: CartesetDataHolder;
    amount: number;
    source: 'explicit' | 'match' | 'fifo';
};

const EPS = 0.05;

const r2 = (n: number) => Math.round(n * 100) / 100;

/* ══════════════════════════════════════════════════════════════════════════
 * Shared allocation core — keep identical in:
 *   nexilcolab/src/views/customer/profile/debts/cartesetLinks.ts
 *   ai_assistent/frontend/src/components/dc/ledgerLinks.ts
 *   ai_assistent/backend/debt_collection/analyzer_findings.py
 * ══════════════════════════════════════════════════════════════════════════ */

export type AllocMark = { id: string; paid: number | null };

export type AllocRow = {
    /** Caller's index — returned in links. */
    i: number;
    /** Integer day (any epoch); only differences matter. */
    day: number;
    /** Same-day tie-break (document number). */
    seq: number;
    docId: string;
    charge: number;
    credit: number;
    open: number;
    marks: AllocMark[];
};

export type AllocLink = { from: number; to: number; amount: number; source: 'explicit' | 'match' | 'fifo' };

export type AllocResult = {
    links: AllocLink[];
    /** Credit nobody consumed (advances, never-allocated receipts). */
    unallocated: Array<{ i: number; left: number }>;
    /** חובה no זכות in the ledger covers. `refund`: a refund the surplus did not cover — owed back. */
    uncovered: Array<{ i: number; left: number; refund?: boolean }>;
    /** Reversing pairs (negative receipt + the receipt it cancelled). */
    cancelled: number[];
    /** Negative receipts that cancelled nothing — money returned to the client (a חובה, not a receipt). */
    refunds: number[];
};

const REVERSAL_DAYS = 60;

function allocSelfClosed(r: AllocRow): boolean {
    return r.charge > EPS && r.credit > EPS && Math.abs(r.charge - r.credit) <= EPS;
}

function allocSnapshot(r: AllocRow): boolean {
    return r.charge > EPS && r.credit > EPS && Math.abs(r.charge - r.credit) > EPS;
}

function allocNeed(r: AllocRow): number {
    if (allocSelfClosed(r)) return 0;
    if (allocSnapshot(r)) return Math.max(0, r2(r.charge - r.credit));
    if (r.charge <= EPS) return 0;
    return Math.max(0, r2(r.charge - Math.max(r.open, 0)));
}

function allocCredit(r: AllocRow): number {
    if (allocSelfClosed(r)) return 0;
    if (allocSnapshot(r)) return Math.max(0, r2(r.credit - r.charge));
    if (r.credit > EPS) return r.credit;
    return r.charge < -EPS ? -r.charge : 0;
}

/** Same day: חובה rows, then זכות rows, then reversals. */
function allocRank(r: AllocRow): number {
    if (r.charge > EPS) return 0;
    if (r.credit > EPS || r.charge < -EPS) return 1;
    return 2;
}

export function allocateLedger(rows: AllocRow[]): AllocResult {
    type Need = { i: number; docId: string; day: number; left: number; refund?: boolean };
    type Credit = { i: number; docId: string; day: number; left: number; note: boolean; marks: AllocMark[] };

    const links: AllocLink[] = [];
    const linkByKey = new Map<string, AllocLink>();
    const push = (from: number, to: number, amount: number, source: AllocLink['source']) => {
        if (from === to || amount <= EPS) return;
        const key = `${from}->${to}`;
        const prev = linkByKey.get(key);
        if (prev) {
            prev.amount = r2(prev.amount + amount);
            if (source === 'explicit' || (source === 'match' && prev.source === 'fifo')) prev.source = source;
            return;
        }
        const link: AllocLink = {from, to, amount: r2(amount), source};
        linkByKey.set(key, link);
        links.push(link);
    };

    const order = rows
        .filter((r) => !allocSelfClosed(r))
        .sort((a, b) => (a.day - b.day) || (allocRank(a) - allocRank(b)) || (a.seq - b.seq) || (a.i - b.i));

    const needs: Need[] = [];
    const credits: Credit[] = [];
    const reversals: AllocRow[] = [];
    const needByI = new Map<number, Need>();
    const creditByI = new Map<number, Credit>();
    for (const r of order) {
        const need = allocNeed(r);
        if (need > EPS) {
            const n: Need = {i: r.i, docId: r.docId, day: r.day, left: need};
            needs.push(n);
            needByI.set(r.i, n);
            continue;
        }
        const amt = allocCredit(r);
        if (amt > EPS) {
            const c: Credit = {
                i: r.i, docId: r.docId, day: r.day, left: amt,
                note: r.credit <= EPS && r.charge < -EPS, marks: r.marks,
            };
            credits.push(c);
            creditByI.set(r.i, c);
            continue;
        }
        if (r.credit < -EPS) reversals.push(r);
    }

    // 1. Reversals: a negative קבלה cancels the קבלה it reverses.
    const cancelled = new Set<number>();
    const refunds: number[] = [];
    for (const rv of reversals) {
        const amt = -rv.credit;
        const ids = new Set(rv.marks.map((m) => m.id));
        let best: Credit | null = null;
        let bestScore = Infinity;
        for (const c of credits) {
            if (c.note || cancelled.has(c.i) || Math.abs(c.left - amt) > EPS) continue;
            const gap = rv.day - c.day;
            if (gap < 0 || gap > REVERSAL_DAYS) continue;
            const overlap = c.marks.some((m) => ids.has(m.id));
            const score = gap === 0 ? 0 : overlap ? 1 : 2 + gap;
            if (score < bestScore) {
                bestScore = score;
                best = c;
            }
        }
        if (best) {
            cancelled.add(best.i);
            cancelled.add(rv.i);
        } else {
            // 1b. A negative קבלה that cancels nothing is a REFUND: money handed
            // back to the client. It is a חובה that eats the surplus credit (an
            // advance, an unallocated receipt) — never a receipt to link, and
            // the credit it returned must not look "unallocated".
            const refundNeed: Need = {i: rv.i, docId: rv.docId, day: rv.day, left: r2(amt), refund: true};
            needs.push(refundNeed);
            needByI.set(rv.i, refundNeed);
            refunds.push(rv.i);
        }
    }

    const needById = new Map<string, Need>();
    // Refund rows are receipts — invoice marks never point at them.
    for (const n of needs) if (n.docId && !n.refund && !needById.has(n.docId)) needById.set(n.docId, n);

    // 2. Marks are the truth: the user said which invoices this money closed.
    for (const c of credits) {
        if (cancelled.has(c.i) || !c.marks.length) continue;
        const offLedger: AllocMark[] = [];
        for (const m of c.marks) {
            if (c.left <= EPS) break;
            const inv = needById.get(m.id);
            if (!inv) {
                offLedger.push(m);
                continue;
            }
            if (inv.left <= EPS) continue;
            const want = m.paid != null ? Math.min(m.paid, inv.left) : inv.left;
            const take = Math.min(want, c.left);
            push(c.i, inv.i, take, 'explicit');
            inv.left = r2(inv.left - take);
            c.left = r2(c.left - take);
        }
        // A marked invoice outside the loaded range still owns its share.
        for (const m of offLedger) {
            if (c.left <= EPS) break;
            const take = m.paid != null ? Math.min(m.paid, c.left) : c.left;
            c.left = r2(c.left - take);
        }
    }

    // 3. Replay oldest → newest: exact amount first, then oldest open.
    const exact = <T extends { left: number }>(pool: T[], amount: number): T | null => {
        for (const x of pool) if (x.left > EPS && Math.abs(x.left - amount) <= EPS) return x;
        return null;
    };
    const openNeeds: Need[] = [];
    const openCredits: Credit[] = [];
    for (const r of order) {
        const need = needByI.get(r.i);
        if (need) {
            if (need.left > EPS) {
                const hit = exact(openCredits, need.left);
                if (hit) {
                    push(hit.i, need.i, need.left, 'match');
                    hit.left = r2(hit.left - need.left);
                    need.left = 0;
                }
                for (const c of openCredits) {
                    if (need.left <= EPS) break;
                    if (c.left <= EPS) continue;
                    const take = Math.min(need.left, c.left);
                    push(c.i, need.i, take, 'fifo');
                    need.left = r2(need.left - take);
                    c.left = r2(c.left - take);
                }
            }
            if (need.left > EPS) openNeeds.push(need);
            continue;
        }
        const credit = creditByI.get(r.i);
        if (!credit || cancelled.has(credit.i)) continue;
        if (credit.left > EPS) {
            const hit = exact(openNeeds, credit.left);
            if (hit) {
                push(credit.i, hit.i, credit.left, 'match');
                hit.left = r2(hit.left - credit.left);
                credit.left = 0;
            }
            for (const n of openNeeds) {
                if (credit.left <= EPS) break;
                if (n.left <= EPS) continue;
                const take = Math.min(n.left, credit.left);
                push(credit.i, n.i, take, 'fifo');
                n.left = r2(n.left - take);
                credit.left = r2(credit.left - take);
            }
        }
        if (credit.left > EPS) openCredits.push(credit);
    }

    return {
        links,
        unallocated: openCredits.filter((c) => c.left > EPS).map((c) => ({i: c.i, left: c.left})),
        uncovered: openNeeds.filter((n) => n.left > EPS).map((n) => (n.refund ? {i: n.i, left: n.left, refund: true} : {i: n.i, left: n.left})),
        cancelled: Array.from(cancelled),
        refunds: refunds.slice().sort((a, b) => a - b),
    };
}

/** `tax_note_id` "101:0.0:1500,205:200.0:700" → marks with the amount paid to each. */
export function parseAllocMarks(raw: unknown): AllocMark[] {
    if (raw == null || raw === '') return [];
    if (typeof raw === 'number') {
        const s = String(raw).trim();
        return s && s !== '0' ? [{id: s, paid: null}] : [];
    }
    if (typeof raw === 'string') {
        const t = raw.trim();
        if (!t || t === '0' || t.toLowerCase() === 'auto') return [];
        const out: AllocMark[] = [];
        for (const tok of t.split(',')) {
            const parts = tok.trim().split(':');
            let id = parts[0].trim();
            if (id.startsWith('-')) id = id.slice(1);
            if (!id || id === '0' || !/^\d+$/.test(id)) continue;
            const paidRaw = parts.length > 2 ? Number(parts[2]) : NaN;
            const paid = Number.isFinite(paidRaw) && Math.abs(paidRaw) > EPS ? r2(Math.abs(paidRaw)) : null;
            out.push({id, paid});
        }
        return out;
    }
    if (!Array.isArray(raw)) return [];
    const out: AllocMark[] = [];
    for (const x of raw) {
        if (x == null) continue;
        if (typeof x === 'string' || typeof x === 'number') {
            out.push(...parseAllocMarks(String(x)));
            continue;
        }
        if (typeof x === 'object') {
            const o = x as { id?: unknown; doc_id?: unknown; paid?: unknown; amount?: unknown };
            let id = String(o.doc_id ?? o.id ?? '').trim();
            if (id.startsWith('-')) id = id.slice(1);
            if (!id || id === '0') continue;
            const p = Number(o.paid ?? o.amount);
            out.push({id, paid: Number.isFinite(p) && Math.abs(p) > EPS ? r2(Math.abs(p)) : null});
        }
    }
    return out;
}

/* ══════════════════════════════════════════════════════════════════════════
 * End of shared core.
 * ══════════════════════════════════════════════════════════════════════════ */

const debitCol = (r: CartesetDataHolder) => Number(r.value_tn) || 0;
const creditCol = (r: CartesetDataHolder) => Number(r.value_pay) || 0;

function dayOf(r: CartesetDataHolder): number {
    const d = r.date;
    if (!d || typeof d.getFullYear !== 'function') return 0;
    return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
}

/**
 * The receipt's own marks. The Kotlin `ClientPay.tax_note_id` string carries
 * the amount paid per invoice ("id:left:paid"); `receiptsIds` is ids only.
 */
function marksOf(r: CartesetDataHolder): AllocMark[] {
    const doc: any = r.doc;
    const raw = doc?.nkObject?.tax_note_id ?? doc?.header?.tax_note_id;
    const fromString = parseAllocMarks(typeof raw === 'string' ? raw : null);
    if (fromString.length) return fromString;
    let ids: any = doc?.receiptsIds;
    if (ids && typeof ids.toArray === 'function') ids = ids.toArray();
    return parseAllocMarks(Array.isArray(ids) ? ids : null);
}

function toAllocRow(r: CartesetDataHolder, i: number): AllocRow {
    const docId = r.id != null && r.id !== 0 ? String(r.id) : '';
    return {
        i,
        day: dayOf(r),
        seq: Number.isFinite(Number(r.id)) ? Number(r.id) : Number.MAX_SAFE_INTEGER,
        docId,
        charge: debitCol(r),
        credit: creditCol(r),
        open: Math.max(Number(r.open_tn) || 0, 0),
        marks: marksOf(r),
    };
}

export function buildCartesetLinks(rows: CartesetDataHolder[]): CartesetLink[] {
    if (!rows?.length) return [];
    const alloc = rows.map((r, i) => (r ? toAllocRow(r, i) : null)).filter((r): r is AllocRow => r != null);
    return allocateLedger(alloc).links.map((l) => ({
        from: rows[l.from],
        to: rows[l.to],
        amount: l.amount,
        source: l.source,
    }));
}
